import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import Avatar from '../gui/avatar';
import {
    InviteChip, InviteSummary, countAnswers, ANSWER_ORDER,
} from './invites';

// «Inviter klubbkamerater» bottom drawer. Tab «Inviter»: the club mates (same scope as
// @-mentions) as a checkbox list, narrowed by the search box, members already invited shown
// with their answer instead. Tab «Svar»: who answered what, and a reminder to those waiting.
const SEARCH_DELAY_MS = 300;
const TABS = ['invite', 'answers'];

const CHANNEL_TEXT = {
    sent: { id: 'terminliste.channel-sent', text: 'sent' },
    skipped: { id: 'terminliste.channel-skipped', text: 'off' },
    failed: { id: 'terminliste.channel-failed', text: 'failed' },
    noAddress: { id: 'terminliste.channel-noAddress', text: 'no e-mail address' },
};

export function Channel({ value }) {
    const meta = CHANNEL_TEXT[value] || CHANNEL_TEXT.skipped;
    return <Text id={meta.id}>{meta.text}</Text>;
}

export function nameOf(user = {}) {
    return [user.firstname, user.lastname].filter(Boolean).join(' ') || user.name || `#${user.id}`;
}

@observer
class NsfCompetitionInviteDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            tab: 'invite', query: '', selected: [], message: '', done: false, reminding: false,
        };
    }

    componentDidMount() {
        const { nsfCompetitionInviteStore, userStore } = this.props.stores;
        if (!nsfCompetitionInviteStore || !this.loggedIn()) {
            return;
        }
        nsfCompetitionInviteStore.clearCandidates();
        nsfCompetitionInviteStore.updateKeyValue('lastResult', null);
        nsfCompetitionInviteStore.loadMembers({ hostname: this.hostname(), selfId: userStore.user.id });
        if (this.props.id) {
            nsfCompetitionInviteStore.loadFor([this.props.id]);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimer);
    }

    hostname() {
        return typeof window !== 'undefined' ? window.location.hostname : '';
    }

    loggedIn() {
        const { userStore } = this.props.stores;
        return !!(userStore && userStore.user && userStore.user.id);
    }

    onQuery = (e) => {
        const query = e.target.value;
        this.setState({ query });
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
            const { nsfCompetitionInviteStore, userStore } = this.props.stores;
            nsfCompetitionInviteStore.searchCandidates(query, {
                hostname: this.hostname(),
                selfId: userStore.user ? userStore.user.id : 0,
            });
        }, SEARCH_DELAY_MS);
    }

    toggle = (user) => {
        this.setState((prev) => {
            const picked = prev.selected.some(u => u.id === user.id);
            return { selected: picked ? prev.selected.filter(u => u.id !== user.id) : [...prev.selected, user] };
        });
    }

    selectAll = (rows) => {
        this.setState((prev) => {
            const next = [...prev.selected];
            rows.forEach((user) => {
                if (!next.some(u => u.id === user.id)) {
                    next.push(user);
                }
            });
            return { selected: next };
        });
    }

    clearSelection = () => {
        this.setState({ selected: [] });
    }

    onMessage = (e) => {
        this.setState({ message: e.target.value });
    }

    send = async () => {
        const { nsfCompetitionInviteStore } = this.props.stores;
        const { id } = this.props;
        const { selected, message } = this.state;
        if (!id || selected.length === 0) {
            return;
        }
        const data = await nsfCompetitionInviteStore.send({
            nsfCompetitionId: parseInt(id, 10), toUserIds: selected.map(u => u.id), message,
        });
        if (data) {
            this.setState({ done: true });
        }
    }

    remind = async () => {
        const { nsfCompetitionInviteStore } = this.props.stores;
        this.setState({ reminding: true });
        await nsfCompetitionInviteStore.remind(parseInt(this.props.id, 10));
        this.setState({ reminding: false });
    }

    inviteMore = () => {
        const { nsfCompetitionInviteStore } = this.props.stores;
        nsfCompetitionInviteStore.updateKeyValue('lastResult', null);
        this.setState({
            done: false, selected: [], message: '', query: '',
        });
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    setTab = (tab) => {
        if (TABS.includes(tab)) {
            this.setState({ tab });
        }
    }

    /** The rows to show: search hits while typing, else the member list. */
    visibleRows() {
        const { nsfCompetitionInviteStore } = this.props.stores;
        const { query } = this.state;
        const { candidates = [], members = [] } = nsfCompetitionInviteStore;
        return query.trim().length >= 2 ? candidates : members;
    }

    renderResult() {
        const { nsfCompetitionInviteStore } = this.props.stores;
        const { selected } = this.state;
        const result = nsfCompetitionInviteStore.lastResult || {};
        const sent = result.sent || [];
        return (
            <div>
                <div class='alert alert-success py-2'>
                    <i class='fa-solid fa-paper-plane me-1' />
                    <Text id='terminliste.invite.sentCount' fields={{ count: sent.length }}>Invitation sent to {sent.length}</Text>
                </div>
                <ul class='list-group list-group-flush small mb-3'>
                    {sent.map((row) => {
                        const user = selected.find(u => u.id === row.toUser) || { id: row.toUser };
                        return (
                            <li key={row.toUser} class='list-group-item px-0 py-1 d-flex justify-content-between' data-to-user={row.toUser}>
                                <span>{nameOf(user)}</span>
                                <span class='text-body-secondary'>
                                    Push: <Channel value={row.push} /> · <Text id='terminliste.invite.email'>E-mail</Text>: <Channel value={row.email} />
                                </span>
                            </li>
                        );
                    })}
                    {(result.skipped || []).map(id => (
                        <li key={`skip-${id}`} class='list-group-item px-0 py-1 text-body-secondary'>
                            {nameOf(selected.find(u => u.id === id) || { id })}: <Text id='terminliste.invite.notEligible'>not in your club</Text>
                        </li>
                    ))}
                </ul>
                <div class='d-flex gap-2'>
                    <button type='button' class='btn btn-sm btn-outline-primary' onClick={this.inviteMore}>
                        <i class='fa-solid fa-user-plus me-1' />
                        <Text id='terminliste.invite.more'>Invite more</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-secondary' onClick={() => this.setTab('answers')}>
                        <Text id='terminliste.invite.answers'>Answers</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-success ms-auto' onClick={this.close}>
                        <Text id='terminliste.done'>Done</Text>
                    </button>
                </div>
            </div>
        );
    }

    renderInvite() {
        const { nsfCompetitionInviteStore } = this.props.stores;
        const { id } = this.props;
        const {
            query, selected, message,
        } = this.state;
        const { searching, loadingMembers, sending } = nsfCompetitionInviteStore;
        const result = nsfCompetitionInviteStore.lastResult;
        const sentById = {};
        nsfCompetitionInviteStore.summaryFor(parseInt(id, 10)).sent.forEach((invite) => { sentById[invite.to.id] = invite; });
        const rows = this.visibleRows();
        const selectable = rows.filter(u => !sentById[u.id]);
        const busy = searching || loadingMembers;
        return (
            <div>
                <div class='mb-2'>
                    <div class='d-flex flex-wrap align-items-center gap-2 mb-1'>
                        <label class='form-label small fw-semibold mb-0' for='nsf-invite-search'>
                            <Text id='terminliste.invite.who'>Who</Text>
                        </label>
                        {selected.length > 0 && (
                            <span class='badge rounded-pill text-bg-success' data-selected-count={selected.length}>
                                <Text id='terminliste.selectedCount' fields={{ count: selected.length }}>{selected.length} selected</Text>
                            </span>
                        )}
                        {selectable.length > 0 && (
                            <button type='button' class='btn btn-sm btn-link py-0 px-1 nsf-invite-select-all' onClick={() => this.selectAll(selectable)}>
                                <Text id='terminliste.invite.selectAll'>Select all</Text>
                            </button>
                        )}
                        {selected.length > 0 && (
                            <button type='button' class='btn btn-sm btn-link py-0 px-1' onClick={this.clearSelection}>
                                <Text id='terminliste.clearSelection'>Clear</Text>
                            </button>
                        )}
                    </div>
                    <Localizer>
                        <input
                            id='nsf-invite-search'
                            class='form-control form-control-sm'
                            type='search'
                            autocomplete='off'
                            value={query}
                            placeholder={<Text id='terminliste.invite.searchPlaceholder'>Search for a club mate</Text>}
                            onInput={this.onQuery}
                        />
                    </Localizer>
                    {busy && <div class='small text-body-secondary mt-1'><i class='fa-solid fa-spinner fa-spin me-1' />…</div>}
                    <div class='list-group mt-1 nsf-invite-members' style='max-height: 40vh; overflow: auto;'>
                        {rows.map((user) => {
                            const already = sentById[user.id];
                            const picked = selected.some(u => u.id === user.id);
                            const inputId = `nsf-invite-member-${user.id}`;
                            return (
                                <label
                                    key={user.id}
                                    class={`list-group-item d-flex align-items-center gap-2 py-1 ${already ? 'text-body-secondary' : 'list-group-item-action'}`}
                                    for={inputId}
                                    data-member={user.id}
                                    data-picked={picked ? 1 : 0}
                                    data-already={already ? already.status : ''}
                                >
                                    {already
                                        ? <span style='width: 1.25rem;' class='d-inline-block' />
                                        : (
                                            <input
                                                id={inputId}
                                                class='form-check-input mt-0'
                                                type='checkbox'
                                                checked={picked}
                                                onChange={() => this.toggle(user)}
                                            />
                                        )}
                                    <Avatar name={nameOf(user)} image={user.image && user.image.s3SmallLink} size={24} />
                                    <span class='flex-grow-1'>{nameOf(user)}</span>
                                    {already && <InviteChip invite={{ ...already, to: { ...already.to, name: '' } }} size={0} />}
                                </label>
                            );
                        })}
                    </div>
                    {!busy && rows.length === 0 && (
                        <div class='small text-body-secondary mt-1'><Text id='terminliste.invite.noResults'>No club mates found.</Text></div>
                    )}
                </div>
                <div class='mb-3'>
                    <label class='form-label small fw-semibold mb-1' for='nsf-invite-message'>
                        <Text id='terminliste.invite.message'>Message (optional)</Text>
                    </label>
                    <Localizer>
                        <textarea
                            id='nsf-invite-message'
                            class='form-control form-control-sm'
                            rows='2'
                            maxlength='500'
                            value={message}
                            placeholder={<Text id='terminliste.invite.messagePlaceholder'>E.g. I am driving at 07:30, room for two.</Text>}
                            onInput={this.onMessage}
                        />
                    </Localizer>
                </div>
                {result && result.error && <div class='alert alert-danger py-2 small'>{result.error}</div>}
                <div class='d-flex gap-2'>
                    <button type='button' class='btn btn-sm btn-success nsf-invite-send' disabled={sending || selected.length === 0} onClick={this.send}>
                        <i class={`fa-solid ${sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`} />
                        <Text id='terminliste.invite.sendCount' fields={{ count: selected.length }}>Send invitation ({selected.length})</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-secondary ms-auto' onClick={this.close}>
                        <Text id='terminliste.invite.cancel'>Cancel</Text>
                    </button>
                </div>
                <div class='small text-body-secondary mt-2'>
                    <Text id='terminliste.invite.howItWorks'>They get a push and an e-mail with a link to the competition, and can answer going / maybe / can't make it.</Text>
                </div>
            </div>
        );
    }

    renderAnswers() {
        const { nsfCompetitionInviteStore } = this.props.stores;
        const { id } = this.props;
        const { reminding } = this.state;
        const { sent } = nsfCompetitionInviteStore.summaryFor(parseInt(id, 10));
        if (sent.length === 0) {
            return (
                <div class='small text-body-secondary'>
                    <Text id='terminliste.invite.noneSent'>You have not invited anyone to this competition yet.</Text>
                </div>
            );
        }
        const counts = countAnswers(sent);
        const groups = ANSWER_ORDER.map(key => ({ key, rows: sent.filter(i => (i.status === key) || (key === 'sent' && !ANSWER_ORDER.slice(0, 3).includes(i.status))) }))
            .filter(g => g.rows.length > 0);
        return (
            <div class='nsf-invite-answers'>
                <div class='d-flex flex-wrap align-items-center gap-2 mb-2'>
                    <span class='fw-semibold small'><InviteSummary sent={sent} /></span>
                    {counts.sent > 0 && (
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill py-0 nsf-invite-remind' disabled={reminding} onClick={this.remind}>
                            <i class={`fa-solid ${reminding ? 'fa-spinner fa-spin' : 'fa-bell'} me-1`} />
                            <Text id='terminliste.invite.remind' fields={{ count: counts.sent }}>Remind {counts.sent} waiting</Text>
                        </button>
                    )}
                </div>
                {groups.map(group => (
                    <div key={group.key} class='mb-2' data-answer-group={group.key}>
                        <div class='d-flex flex-wrap gap-1'>
                            {group.rows.map(invite => <InviteChip key={invite.id} invite={invite} size={24} />)}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { title } = this.props;
        const { tab, done } = this.state;
        if (!this.loggedIn()) {
            return (
                <div class='container py-3'>
                    <div class='alert alert-info'><Text id='terminliste.invite.loginToInvite'>Log in to invite club mates.</Text></div>
                </div>
            );
        }
        return (
            <div class='container py-3' style='padding-bottom: 80px;'>
                <h5 class='mb-1'><Text id='terminliste.invite.title'>Invite to competition</Text></h5>
                {title && <div class='small text-body-secondary mb-2'>{title}</div>}
                <ul class='nav nav-pills mb-3'>
                    <li class='nav-item'>
                        <button type='button' class={`nav-link ${tab === 'invite' ? 'active' : ''}`} onClick={() => this.setTab('invite')}>
                            <Text id='terminliste.invite.tabInvite'>Invite</Text>
                        </button>
                    </li>
                    <li class='nav-item'>
                        <button type='button' class={`nav-link ${tab === 'answers' ? 'active' : ''}`} onClick={() => this.setTab('answers')}>
                            <Text id='terminliste.invite.answers'>Answers</Text>
                        </button>
                    </li>
                </ul>
                {tab === 'answers' && this.renderAnswers()}
                {tab === 'invite' && (done ? this.renderResult() : this.renderInvite())}
            </div>
        );
    }
}

export default NsfCompetitionInviteDrawer;
